import { plainToInstance } from 'class-transformer';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import nookies from 'nookies';

import AdBanner from '@/components/AdBanner';
import CurrentForecastWidget from '@/components/CurrentForecastWidget';
import OtherPlacesForecastWidget from '@/components/OtherPlacesForecastWidget';
import QuarteredForecastWidget from '@/components/QuarteredForecastWidget';
import { SAVED_PLACE_COOKIE } from '@/contexts/SavedPlaceContext';
import { BannerEnum } from '@/data/banners';
import Meta from '@/layouts/Meta';
import { CurrentForecast } from '@/models/CurrentForecast';
import { HourlyForecast } from '@/models/HourlyForecast';
import { OtherPlaceForecast } from '@/models/OtherPlaceForecast';
import { QuarteredForecast } from '@/models/QuarteredForecast';
import { getAllForecasts } from '@/providers/ForecastProvider';
import Main from '@/templates/Main';
import NpsWidgetScript, { NpsTypes } from '@/templates/NpsWidgetScript';
import { AppConfig } from '@/utils/AppConfig';
import { completeSeoData, SeoData } from '@/utils/Seo';
import { SeoPlaceholders } from '@/utils/SeoPlaceholders';

import type { AllForecastsJsonType } from '@/providers/ForecastProvider';

type IProps = {
  forecasts: AllForecastsJsonType;
  seo: SeoData;
};

const rawSeoData: SeoData = {
  title: `Погода ${SeoPlaceholders.CITY_GENITIVE} точный прогноз на сегодня по часам`,
  description: `Прогноз погоды ${SeoPlaceholders.CITY_GENITIVE} на сегодня. Информация о погоде по часам, на завтра, неделю и на 10 дней.\
 Подробно о ветре, осадках, давлении, влажности и температуре ${SeoPlaceholders.CITY_GENITIVE}, ${SeoPlaceholders.SUBJECT}\
 - ${SeoPlaceholders.BRAND_NAME}`,
};

export const getServerSideProps: GetServerSideProps<IProps> = async (ctx) => {
  const placeSlug = ctx.params?.place?.toString() ?? 'vladivostok'; // shitty params type
  const place = AppConfig.partner.getPlaceBySlug(placeSlug);

  if (!place) {
    return {
      notFound: true,
    };
  }

  nookies.set(ctx, SAVED_PLACE_COOKIE, placeSlug, { path: AppConfig.basePath });

  return {
    props: {
      forecasts: await getAllForecasts(place),
      seo: completeSeoData(rawSeoData, place),
    },
  };
};

const PlaceForecast = ({ forecasts, seo }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const currentForecast = plainToInstance(CurrentForecast, forecasts.current);
  const hourlyForecast = plainToInstance(HourlyForecast, forecasts.hourly);
  const quarteredForecast = plainToInstance(QuarteredForecast, forecasts.quartered);
  const otherPlacesForecast = plainToInstance(OtherPlaceForecast, forecasts.other);
  const beforeFooterBanner = AppConfig.partner.banners[BannerEnum.BEFORE_FOOTER];

  return (
    <Main meta={<Meta title={seo.title} description={seo.description} />}>
      <CurrentForecastWidget currentForecast={currentForecast} hourlyForecast={hourlyForecast} />
      <QuarteredForecastWidget quarteredForecast={quarteredForecast} />
      <OtherPlacesForecastWidget otherPlacesForecast={otherPlacesForecast} />
      {AppConfig.partner.isDvHab() && (
        <AdBanner
          dataId={beforeFooterBanner.id}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={`max-w-[${beforeFooterBanner.width}] mx-auto mt-6 overflow-hidden`}
        />
      )}
      <NpsWidgetScript type={NpsTypes.QUESTION} slug={'vl_weather_main'} />
    </Main>
  );
};

export default PlaceForecast;
